import { Popover as BasePopover } from '@base-ui/react/popover';
import { useState, type ReactElement, type ReactNode } from 'react';

import arrowStyles from '../shared/floating-arrow.module.css';
import styles from './Popover.module.css';

export type PopoverSide = 'top' | 'bottom' | 'left' | 'right';
export type PopoverAlign = 'start' | 'center' | 'end';

export interface PopoverContentControls {
  /** 收起当前气泡。 */
  close: () => void;
}

export interface PopoverProps {
  children: ReactElement;
  className?: string;
  /*
   * 气泡内容。写成函数时会收到 `close()`：悬浮展开的气泡要点完里面的按钮立刻收起就用它——
   * 否则鼠标还停在气泡上，它会一直挂在那儿挡住刚聚焦过去的输入框。
   */
  content: ReactNode | ((controls: PopoverContentControls) => ReactNode);
  /*
   * 悬停展开的开关三条：`openOnHover` 决定「移上去就开」，
   * `hoverDelay` 是移上触发器后的等待时间，`hoverCloseDelay` 是移开后的停留时间——
   * 必须留这段尾巴，用户才来得及把鼠标从触发器挪进气泡里点按钮。
   *
   * 气泡里要放可点击内容（比如报错清单里的「处理」）时只能用 Popover：
   * Tooltip 的浮层不可交互，鼠标还没移过去它就关了。
   */
  openOnHover?: boolean;
  hoverDelay?: number;
  hoverCloseDelay?: number;
  /** 受控展开状态；不传则由组件内部自己记住开关。 */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: PopoverSide;
  align?: PopoverAlign;
  title?: string;
}

export function Popover({
  align = 'start',
  children,
  className,
  content,
  hoverCloseDelay = 240,
  hoverDelay = 150,
  onOpenChange,
  open,
  openOnHover,
  side = 'bottom',
  title,
}: PopoverProps) {
  /*
   * 未受控时自己记一份开关：不是为了控制展开时机（那归 Base UI 管），
   * 而是为了让气泡内容能用自己的 `close()` 收起——没有这份 state 就命令不了它。
   */
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;

  function handleOpenChange(next: boolean) {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  }

  return (
    <BasePopover.Root onOpenChange={handleOpenChange} open={isControlled ? open : internalOpen}>
      {/* openOnHover / delay / closeDelay 挂在 Trigger 上而不是 Root：这三个决定的是「怎么悬停」。 */}
      <BasePopover.Trigger
        closeDelay={hoverCloseDelay}
        delay={hoverDelay}
        openOnHover={openOnHover}
        render={children}
      />
      <BasePopover.Portal>
        <BasePopover.Positioner align={align} className={styles.positioner} side={side} sideOffset={6}>
          <BasePopover.Popup className={[styles.popup, className].filter(Boolean).join(' ')}>
            {title ? <p className={styles.title}>{title}</p> : null}
            <div className={styles.content}>{typeof content === 'function' ? content({ close: () => handleOpenChange(false) }) : content}</div>
            <BasePopover.Arrow className={[styles.arrow, arrowStyles.arrow].join(' ')} />
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}
